import * as XLSX from 'xlsx';

type Row = Record<string, unknown>;
type Format = (key: string, value: unknown) => string;

/** Celula da planilha: texto, ou numero com o formato do Excel (data, moeda, percentual). */
export interface ExportCell {
    t: 's' | 'n';
    v: string | number;
    z?: string;
}

const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T/;
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 86_400_000;
/** 1970-01-01 na contagem de dias do Excel. */
const EXCEL_EPOCH = 25569;

const excelSerial = (utcMs: number) => utcMs / DAY_MS + EXCEL_EPOCH;

/** Instante no relogio do fuso (o Excel nao tem fuso: guarda o que a celula mostra). */
function localSerial(iso: string, timeZone: string): number {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
    }).formatToParts(new Date(iso));
    const n = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === type)?.value);
    return excelSerial(Date.UTC(n('year'), n('month') - 1, n('day'), n('hour'), n('minute'), n('second')));
}

function cellFor(key: string, raw: unknown, format: Format, timeZone: string): ExportCell {
    if (raw === null || raw === undefined || raw === '') return { t: 's', v: '' };
    if (typeof raw === 'string' && ISO_INSTANT.test(raw)) return { t: 'n', v: localSerial(raw, timeZone), z: 'dd/mm/yyyy hh:mm' };
    if (typeof raw === 'string' && ISO_DAY.test(raw)) {
        const [y, m, d] = raw.split('-').map(Number);
        return { t: 'n', v: excelSerial(Date.UTC(y, m - 1, d)), z: 'dd/mm/yyyy' };
    }
    if (typeof raw === 'number') {
        // O formato da tela diz o que o numero e: moeda e percentual seguem numericos no Excel.
        const text = format(key, raw);
        if (/^-?R\$/.test(text.replace(/\s/g, ''))) return { t: 'n', v: raw, z: '"R$" #,##0.00' };
        if (text.endsWith('%')) return { t: 'n', v: Number((raw / 100).toFixed(6)), z: '0.0%' };
        return { t: 'n', v: raw, z: Number.isInteger(raw) ? '#,##0' : '#,##0.0#' };
    }
    return { t: 's', v: format(key, raw) };
}

/**
 * Planilha igual a tabela da tela: as mesmas colunas, na mesma ordem, com os mesmos titulos
 * e os mesmos textos (status, situacao, Sim/Não). Datas, valores e percentuais seguem como
 * numero no Excel, no horario de Brasilia.
 */
export function buildExportSheet(
    columns: Array<{ key: string; label: string }>,
    rows: Row[],
    format: Format,
    timeZone: string,
): ExportCell[][] {
    const header = columns.map((c): ExportCell => ({ t: 's', v: c.label }));
    return [header, ...rows.map((r) => columns.map((c) => cellFor(c.key, r[c.key], format, timeZone)))];
}

/** Baixa a planilha montada por `buildExportSheet`. */
export function downloadXlsx(filename: string, sheetName: string, cells: ExportCell[][]): void {
    const ws: XLSX.WorkSheet = {};
    cells.forEach((row, r) =>
        row.forEach((cell, c) => {
            ws[XLSX.utils.encode_cell({ r, c })] = { ...cell };
        }),
    );
    const cols = cells[0]?.length ?? 0;
    ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: Math.max(cells.length - 1, 0), c: Math.max(cols - 1, 0) } });
    ws['!cols'] = (cells[0] ?? []).map((h) => ({ wch: Math.max(14, String(h.v).length + 4) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31) || 'Dados');
    XLSX.writeFile(wb, filename);
}
